import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Kanban,
  Briefcase,
  MapPin,
  Clock,
  AlertTriangle,
  XCircle,
  ChevronRight,
  Loader2,
  Trophy,
  Target,
  CheckCircle,
  Eye,
  Ban
} from "lucide-react";

const COLUMN_CONFIG = {
  applied: { label: "Applied", icon: Clock, accent: "border-blue-500/40", badge: "bg-blue-500/20 text-blue-400" },
  screening: { label: "Screening", icon: Eye, accent: "border-purple-500/40", badge: "bg-purple-500/20 text-purple-400" },
  shortlisted: { label: "Shortlisted", icon: Target, accent: "border-amber-500/40", badge: "bg-amber-500/20 text-amber-400" },
  interview: { label: "Interview", icon: Briefcase, accent: "border-cyan-500/40", badge: "bg-cyan-500/20 text-cyan-400" },
  offer: { label: "Offer", icon: Trophy, accent: "border-emerald-500/40", badge: "bg-emerald-500/20 text-emerald-400" },
  hired: { label: "Hired", icon: CheckCircle, accent: "border-green-500/40", badge: "bg-green-500/20 text-green-400" },
  rejected: { label: "Rejected", icon: XCircle, accent: "border-rose-500/40", badge: "bg-rose-500/20 text-rose-400" },
};

const ApplicationBoard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);

  useEffect(() => {
    fetchBoard();
  }, []);

  const fetchBoard = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/jobs/applications/board`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setBoard(data.board);
        setStats(data.stats);
      } else {
        toast.error("Failed to load applications");
      }
    } catch {
      toast.error("Failed to load board");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (applicationId) => {
    setWithdrawing(applicationId);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API}/jobs/applications/${applicationId}/withdraw`, {
        method: "PUT", credentials: "include", headers,
      });
      if (res.ok) {
        toast.success("Application withdrawn");
        fetchBoard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to withdraw");
      }
    } catch {
      toast.error("Failed to withdraw");
    } finally {
      setWithdrawing(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="board-title">
              <Kanban className="w-7 h-7 text-crimson" />
              Application Tracker
            </h1>
            <p className="text-gray-400 mt-1 text-sm">Track your job applications across every stage</p>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: "Total", value: stats.total_applications, color: "text-white" },
                { label: "Active", value: stats.active, color: "text-blue-400" },
                { label: "Interviews", value: stats.interviews, color: "text-cyan-400" },
                { label: "Offers", value: stats.offers, color: "text-emerald-400" },
                { label: "Hired", value: stats.hired, color: "text-green-400" },
              ].map((s) => (
                <div key={s.label} className="bg-obsidian-light border border-[#222] p-3 rounded" data-testid={`stat-${s.label.toLowerCase()}`}>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Kanban Board */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          ) : !board || Object.values(board).every((col) => col.cards.length === 0) ? (
            <div className="text-center py-20 text-gray-500">
              <Kanban className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No applications yet</p>
              <p className="text-xs text-gray-600 mt-1">Apply to jobs and track your progress here</p>
              <Button onClick={() => navigate("/jobs")} className="mt-4 bg-crimson hover:bg-crimson/90 text-white" data-testid="browse-jobs-btn">
                Browse Jobs
              </Button>
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x">
              {Object.entries(COLUMN_CONFIG).map(([colId, config]) => {
                const column = board[colId];
                if (!column) return null;
                const Icon = config.icon;

                return (
                  <div
                    key={colId}
                    className={`flex-shrink-0 w-72 sm:w-80 bg-obsidian-light border border-[#222] rounded-lg ${config.accent} border-t-2 snap-start`}
                    data-testid={`column-${colId}`}
                  >
                    {/* Column header */}
                    <div className="px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold">{config.label}</h3>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${config.badge} font-medium`}>
                        {column.cards.length}
                      </span>
                    </div>

                    {/* Cards */}
                    <div className="px-3 pb-3 space-y-2 max-h-[60vh] overflow-y-auto">
                      {column.cards.map((card, idx) => (
                        <motion.div
                          key={card.application_id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.03 }}
                          className="bg-obsidian border border-[#333] rounded p-3 space-y-2 group hover:border-[#555] transition-colors"
                          data-testid={`card-${card.application_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <Link to={`/jobs/${card.job_id}`} className="text-sm font-medium group-hover:text-crimson transition-colors line-clamp-2">
                              {card.job_title}
                            </Link>
                            {card.is_urgent && (
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" title="Urgently Hiring" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{card.company_name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            {card.location && (
                              <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{card.location}</span>
                            )}
                            {card.applied_at && (
                              <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{formatDate(card.applied_at)}</span>
                            )}
                          </div>
                          {card.status_note && (
                            <p className="text-xs text-gray-400 italic border-l-2 border-[#444] pl-2">{card.status_note}</p>
                          )}
                          {/* Withdraw button for active applications */}
                          {!["hired", "rejected", "withdrawn"].includes(card.status) && (
                            <button
                              onClick={() => handleWithdraw(card.application_id)}
                              disabled={withdrawing === card.application_id}
                              className="text-xs text-gray-600 hover:text-rose-400 flex items-center gap-1 transition-colors opacity-0 group-hover:opacity-100"
                              data-testid={`withdraw-${card.application_id}`}
                            >
                              {withdrawing === card.application_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
                              Withdraw
                            </button>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ApplicationBoard;
